/**
 * @file parseDraft.js
 * @description Pure mapping from a saved ZATCA invoice payload → UI form state.
 *
 * Inverse of buildPayload.js — used when loading an existing draft for editing.
 * Behaviour preserved exactly from the original inline mapping inside the
 * draft-loading useEffect in invoice-creation-wizard/index.jsx.
 *
 * No React. No state. No side effects.
 */

import { calculateTotals, calculateVatBreakdown } from "./totals";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const mapInvoiceTypeFromCode = (code) => {
  switch (code) {
    case "381":
      return "credit-note";
    case "383":
      return "debit-note";
    case "384":
      return "simplified";
    default:
      return "standard";
  }
};

const mapPaymentModeFromCode = (code) => {
  switch (code) {
    case "10":
      return "Cash";
    case "20":
      return "Cheque";
    case "48":
      return "Credit Card";
    case "30":
      return "Bank Deposit";
    default:
      return "";
  }
};

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

/**
 * Map a saved ZATCA invoice payload to the wizard's formData shape.
 *
 * @param {object} original - Parsed invoice.originalData from the backend
 * @returns {{
 *   customer:       object,
 *   seller:         object,
 *   invoice:        object,
 *   lineItems:      Array,
 *   totals:         object,
 *   invoiceTotals:  object,
 *   vatBreakdown:   Array,
 * }}
 */
export const parseDraftToFormData = (original) => {
  const lineItems = (original.invoiceLines ?? []).map((line, index) => ({
    id: index + 1,
    description: line.item?.name || "",
    quantity: line.invoicedQuantity || 1,
    unitPrice: line.price?.priceAmount || 0,
    taxRate: line.item?.classifiedTaxCategory?.percent ?? 15,
    taxExempt: line.item?.classifiedTaxCategory?.id !== "S",
    vatCategoryCode: line.item?.classifiedTaxCategory?.id || "S",
    exemptionReasonCode:
      line.item?.classifiedTaxCategory?.exemptionReasonCode || "",
    exemptionReason: line.item?.classifiedTaxCategory?.exemptionReason || "",
    exemptionCategory: "",
    discount: 0,
    discountType: "percentage",
    subtotal: 0,
    taxAmount: 0,
    total: 0,
    allowanceChargeIndicator:
      line.allowancesCharges?.[0]?.chargeIndicator !== undefined
        ? String(line.allowancesCharges[0].chargeIndicator)
        : "",
    allowanceChargeReason:
      line.allowancesCharges?.[0]?.allowanceChargeReason || "",
    allowanceChargeReasonCode:
      line.allowancesCharges?.[0]?.allowanceChargeReasonCode || "",
    allowanceChargeAmount: line.allowancesCharges?.[0]?.amount || 0,
    allowanceChargeBaseAmount: line.allowancesCharges?.[0]?.baseAmount || 0,
    allowanceChargeMultiplier:
      line.allowancesCharges?.[0]?.multiplierFactorNumeric || 0,
  }));

  const totals = calculateTotals(lineItems);
  const vatBreakdown = calculateVatBreakdown(lineItems);

  const customer = {
    clientId:
      original.clientId?._id ||
      original.clientId?.id ||
      original.clientId ||
      "",
    type: original.buyer?.vatNumber ? "company" : "individual",
    name: original.buyer?.registrationName || "",
    vatNumber: original.buyer?.vatNumber || "",
    crNumber: original.buyer?.registrationNumber?.id || "",
    registrationNumber: {
      schemeId: original.buyer?.registrationNumber?.schemeId || "",
    },
    contact: {
      email: original.buyer?.contact?.email || "",
      phone: original.buyer?.contact?.phone || "",
    },
    address: {
      street: original.buyer?.address?.streetName || "",
      district: original.buyer?.address?.additionalStreetName || "",
      buildingNumber: original.buyer?.address?.buildingNumber || "",
      plotIdentification: original.buyer?.address?.plotIdentification || "",
      city: original.buyer?.address?.cityName || "",
      citySubdivision: original.buyer?.address?.citySubdivisionName || "",
      province: original.buyer?.address?.countrySubentity || "",
      postalCode: original.buyer?.address?.postalZone || "",
      country: original.buyer?.address?.countryCode || "SA",
    },
  };

  const seller = {
    registrationName: original.seller?.registrationName || "",
    vatNumber: original.seller?.vatNumber || "",
    registrationNumber: {
      id: original.seller?.registrationNumber?.id || "",
      schemeId: original.seller?.registrationNumber?.schemeId || "CRN",
    },
    address: {
      street: original.seller?.address?.streetName || "",
      district: original.seller?.address?.additionalStreetName || "",
      buildingNumber: original.seller?.address?.buildingNumber || "",
      plotIdentification: original.seller?.address?.plotIdentification || "",
      city: original.seller?.address?.cityName || "",
      citySubdivision: original.seller?.address?.citySubdivisionName || "",
      province: original.seller?.address?.countrySubentity || "",
      countrySubentity: original.seller?.address?.countrySubentity || "",
      postalCode: original.seller?.address?.postalZone || "",
      countryCode: original.seller?.address?.countryCode || "SA",
    },
  };

  const invoice = {
    type: mapInvoiceTypeFromCode(original.invoiceHeader?.invoiceTypeCode),
    date: original.invoiceHeader?.issueDate,
    time: original.invoiceHeader?.issueTime,
    currency: original.invoiceHeader?.documentCurrencyCode || "SAR",
    notes: original.invoiceHeader?.notes?.[0] || "",
    paymentMode: mapPaymentModeFromCode(
      original.paymentMeans?.[0]?.paymentMeansCode,
    ),
    paymentNote: original.paymentMeans?.[0]?.paymentNote || "",
    iban: original.paymentMeans?.[0]?.payeeFinancialAccount?.iban || "",
    actualDeliveryDate: original.delivery?.actualDeliveryDate || "",
    latestDeliveryDate: original.delivery?.latestDeliveryDate || "",
    dueDate: original.delivery?.dueDate || "",
    referenceNumber:
      original.billingReference?.invoiceDocumentReference?.id ||
      original.billingReference?.[0]?.invoiceDocumentReference?.id ||
      "",
  };

  const invoiceTotals = {
    lineExtensionAmount: totals.subtotal,
    allowanceTotalAmount: totals.totalDiscount,
    chargeTotalAmount: 0,
    taxExclusiveAmount: totals.taxableAmount,
    taxInclusiveAmount: totals.total,
    prepaidAmount: 0,
    payableRoundingAmount: 0,
    payableAmount: totals.total,
  };

  return {
    customer,
    seller,
    invoice,
    lineItems,
    totals,
    invoiceTotals,
    vatBreakdown,
  };
};
